import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { createUser, fetchStores } from "../api/api";
import { useUser } from "../UserContext";

const SignUp = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [stores, setStores] = useState([]);
    const [message, setMessage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        username: "",
        email: "",
        password: "",
        group: "",
        is_staff: false,
        is_superuser: false,
        default_store: "",
        allowed_store_ids: [],
        allow_store_switching: false,
    });

    useEffect(() => {
        fetchStores()
            .then((data) => setStores(Array.isArray(data) ? data : (data?.results || [])))
            .catch(() => setMessage({type: "danger", text: "Unable to load stores."}));
    }, []);

    const storeOptions = useMemo(
        () => stores.map((store) => ({value: store.id, label: store.name})),
        [stores]
    );

    const handleDefaultStore = (value) => {
        const allowed = form.allowed_store_ids.includes(value)
            ? form.allowed_store_ids
            : [...form.allowed_store_ids, value];
        setForm({...form, default_store: value, allowed_store_ids: allowed});
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            await createUser(form);
            setMessage({type: "success", text: "User created with store access assigned."});
            setTimeout(() => navigate(-1), 700);
            // waits 700 ms after successfully creating a user allowing a success message to appear briefly before returning
            // to the previous page
        } catch (error) {
            const data = error.response?.data;
            const detail = data && typeof data === "object"
                ? Object.values(data).flat().join(" ")
                : "Unable to create user.";
            setMessage({type: "danger", text: detail});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container className="mt-4">
            <Card>
                <Card.Header><strong>Create User</strong></Card.Header>
                <Card.Body>
                    {message && <Alert variant={message.type}>{message.text}</Alert>}
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control required value={form.username}
                                        onChange={(e) => setForm({...form, username: e.target.value})}/>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control type="email" value={form.email}
                                        onChange={(e) => setForm({...form, email: e.target.value})}/>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control type="password" required minLength={8} value={form.password}
                                        onChange={(e) => setForm({...form, password: e.target.value})}/>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role</Form.Label>
                                    <Form.Select value={form.group}
                                        onChange={(e) => setForm({...form, group: e.target.value})}>
                                        <option value="">No group</option>
                                        <option value="Administrator">Administrator</option>
                                        <option value="Manager">Manager</option>
                                        <option value="Accountant">Accountant</option>
                                        <option value="Inventory Manager">Inventory Manager</option>
                                        <option value="Cashier">Cashier</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Default Store</Form.Label>
                                    <Form.Select required value={form.default_store}
                                        onChange={(e) => handleDefaultStore(Number(e.target.value))}>
                                        <option value="">Select store</option>
                                        {stores.map((store) => (
                                            <option key={store.id} value={store.id}>{store.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Allowed Stores</Form.Label>
                                    <Select isMulti options={storeOptions}
                                        value={storeOptions.filter((option) => form.allowed_store_ids.includes(option.value))}
                                        onChange={(options) => setForm({
                                            ...form,
                                            allowed_store_ids: options.map((option) => option.value),
                                        })}/>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Check className="mb-2" label="Allow this user to switch between assigned stores"
                            checked={form.allow_store_switching}
                            onChange={(e) => setForm({...form, allow_store_switching: e.target.checked})}/>
                        {user?.is_superuser && (
                            <>
                                <Form.Check className="mb-2" label="Staff account"
                                    checked={form.is_staff}
                                    onChange={(e) => setForm({...form, is_staff: e.target.checked})}/>
                                <Form.Check className="mb-3" label="Superuser"
                                    checked={form.is_superuser}
                                    onChange={(e) => setForm({...form, is_superuser: e.target.checked})}/>
                            </>
                        )}
                        <Button type="submit" disabled={saving}>
                            {saving ? "Creating..." : "Create User"}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default SignUp;
